import { useNavigate } from "react-router-dom";
import { ChevronDownIcon, GearIcon, ExitIcon, EnvelopeClosedIcon, SunIcon, MoonIcon } from "@radix-ui/react-icons";
import { useUser } from "../../../contexts/UserContext";
import { Dropdown, DropdownItem, DropdownSeparator } from "../../ui";
import { PATHS } from "../../../constants/Paths";
import { useTheme } from "../../../hooks";

/**
 * UserMenu - an actual menu.
 *
 * It used to be a bare <div> with an avatar and a name: no click target, no
 * dropdown, nothing. Settings and logout were reachable only from the sidebar,
 * and clicking your own name - the most universal pattern in any SaaS header -
 * did nothing.
 */
export default function UserMenu() {
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate(PATHS.LOGIN);
    }
  };

  return (
    <Dropdown
      align="right"
      trigger={
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-500"
          aria-label="Account menu"
        >
          <img
            src={user?.avatar || "https://placehold.co/36x36"}
            alt=""
            className="w-8 h-8 rounded-full object-cover bg-muted shrink-0"
          />
          <span className="hidden sm:inline max-w-[10rem] truncate text-sm font-medium">
            {user?.name || "User"}
          </span>
          <ChevronDownIcon className="hidden sm:block h-4 w-4 text-muted-foreground" />
        </button>
      }
    >
      <div className="px-3 py-2 border-b border-border">
        <p className="text-sm font-medium text-foreground truncate">{user?.name || "User"}</p>
        <p className="text-xs text-muted-foreground truncate">{user?.email || ""}</p>
      </div>
      <DropdownItem onClick={() => navigate(PATHS.SETTINGS)}>
        <span className="flex items-center gap-2"><GearIcon className="h-4 w-4 text-muted-foreground" /> Settings</span>
      </DropdownItem>
      <DropdownItem onClick={() => navigate(PATHS.SUPPORT)}>
        <span className="flex items-center gap-2"><EnvelopeClosedIcon className="h-4 w-4 text-muted-foreground" /> Support</span>
      </DropdownItem>
      {/* The label names what the click DOES, not the state it is in. A row
          reading "Dark" leaves you guessing whether that is the current theme
          or the one you are about to get. */}
      <DropdownItem onClick={toggleTheme}>
        <span className="flex items-center gap-2">
          {theme === 'dark'
            ? <SunIcon className="h-4 w-4 text-muted-foreground" />
            : <MoonIcon className="h-4 w-4 text-muted-foreground" />}
          {theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
        </span>
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem onClick={handleLogout}>
        <span className="flex items-center gap-2 text-destructive"><ExitIcon className="h-4 w-4" /> Log out</span>
      </DropdownItem>
    </Dropdown>
  );
}
